import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SpreadsheetService } from '../../../../shared/components/spreadsheet/spreadsheet.service';
import { CalculationResult, CellDefinition, Worksheet } from '../../../../shared/components/spreadsheet/spreadsheet.models';
import { evaluateCustomFormula } from '../../../../shared/utils/custom-test-formula';

// ── Domain model ─────────────────────────────────────────────────────────────

interface CustomInputParameter {
  name: string;
  key?: string | null;
  unit: string;
}

interface CustomOutputParameter {
  name: string;
  key?: string | null;
  unit: string;
  formula?: string | null;
}

interface CustomTableColumn {
  name: string;
  key?: string | null;
  kind: 'input' | 'output';
  unit?: string | null;
  formula?: string | null;
}

interface CustomTableDefinition {
  name: string;
  key?: string | null;
  rowCount: number;
  columnCount?: number;
  columns: CustomTableColumn[];
}

interface CustomTestDefinition {
  testName: string;
  inputParameters: CustomInputParameter[];
  outputParameters: CustomOutputParameter[];
  table?: CustomTableDefinition | null;
  spreadsheet?: Worksheet | null;
}

interface SpreadsheetCellView {
  address: string;
  row: number;
  column: number;
  definition: CellDefinition | null;
}

// ── Component ─────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-job-request-test-custom-detail',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './job-request-test-custom-detail.component.html',
  styleUrl: './job-request-test-custom-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobRequestTestCustomDetailComponent implements OnInit {
  @Input({ required: true }) testKey = '';
  @Input({ required: true }) definitionJson = '';
  @Input({ required: true }) fieldValueByKey!: (testKey: string, fieldKey: string) => string;
  @Output() fieldChanged = new EventEmitter<{ fieldKey: string; value: string }>();

  protected definition: CustomTestDefinition | null = null;
  protected readonly inputValues = signal<Record<string, string>>({});
  protected readonly manualOutputValues = signal<Record<string, string>>({});
  protected readonly spreadsheetWorksheet = signal<Worksheet | null>(null);
  protected readonly spreadsheetCalculation = signal<CalculationResult>({ values: {}, errors: {} });
  protected readonly spreadsheetCells = computed<SpreadsheetCellView[]>(() => {
    const worksheet = this.spreadsheetWorksheet();
    if (!worksheet) return [];

    const cells: SpreadsheetCellView[] = [];
    for (let row = 0; row < worksheet.rows; row++) {
      for (let column = 0; column < worksheet.columns; column++) {
        const address = this.spreadsheetService.indexesToCellAddress(row, column);
        cells.push({
          address,
          row,
          column,
          definition: worksheet.cells[address] ?? null,
        });
      }
    }

    return cells;
  });

  private readonly spreadsheetService = inject(SpreadsheetService);

  protected parameterKey(param: CustomInputParameter | CustomOutputParameter): string {
    return (param.key ?? '').trim() || param.name;
  }

  protected tableColumnKey(table: CustomTableDefinition, column: CustomTableColumn): string {
    return (column.key ?? '').trim() || column.name;
  }

  protected tableCellKey(table: CustomTableDefinition, rowIndex: number, column: CustomTableColumn): string {
    const tableKey = (table.key ?? '').trim() || table.name;
    return `${tableKey}__r${rowIndex + 1}__${this.tableColumnKey(table, column)}`;
  }

  protected tableRowIndices(rowCount: number): number[] {
    return Array.from({ length: Math.max(0, rowCount) }, (_, index) => index);
  }

  protected isTableColumnOutput(column: CustomTableColumn): boolean {
    return column.kind === 'output';
  }

  ngOnInit(): void {
    try {
      this.definition = JSON.parse(this.definitionJson) as CustomTestDefinition;
    } catch {
      this.definition = null;
    }

    if (!this.definition) return;

    if (this.definition.spreadsheet) {
      const worksheet = this.createRuntimeSpreadsheet(this.definition.spreadsheet);
      this.spreadsheetWorksheet.set(worksheet);
      this.spreadsheetCalculation.set(this.spreadsheetService.recalculate(worksheet));
    }

    // Pre-populate inputs from persisted values
    const initial: Record<string, string> = {};
    for (const param of this.definition.inputParameters) {
      const fieldKey = this.parameterKey(param);
      initial[fieldKey] = this.fieldValueByKey(this.testKey, fieldKey);
    }
    this.inputValues.set(initial);

    const initialManualOutputs: Record<string, string> = {};
    for (const param of this.definition.outputParameters ?? []) {
      if (!this.hasFormula(param.formula)) {
        const fieldKey = this.parameterKey(param);
        initialManualOutputs[fieldKey] = this.fieldValueByKey(this.testKey, fieldKey);
      }
    }

    if (this.definition.table) {
      for (const rowIndex of this.tableRowIndices(this.definition.table.rowCount)) {
        for (const column of this.definition.table.columns ?? []) {
          const fieldKey = this.tableCellKey(this.definition.table, rowIndex, column);
          if (column.kind === 'input') {
            initial[fieldKey] = this.fieldValueByKey(this.testKey, fieldKey);
          } else if (!this.hasFormula(column.formula)) {
            initialManualOutputs[fieldKey] = this.fieldValueByKey(this.testKey, fieldKey);
          }
        }
      }
    }

    this.manualOutputValues.set(initialManualOutputs);
  }

  protected onInputChange(fieldKey: string, value: string): void {
    this.inputValues.update(prev => ({ ...prev, [fieldKey]: value }));
    this.fieldChanged.emit({ fieldKey, value });
  }

  protected spreadsheetCellValue(cell: SpreadsheetCellView): string {
    if (!cell.definition) return '';

    if (cell.definition.type === 'formula') {
      if (this.spreadsheetCalculation().errors[cell.address]) return '#ERROR!';
      return this.valueAsString(this.spreadsheetCalculation().values[cell.address]);
    }

    return this.valueAsString(cell.definition.value);
  }

  protected onSpreadsheetNumberChange(address: string, value: string): void {
    const worksheet = this.spreadsheetWorksheet();
    const cell = worksheet?.cells[address];
    if (!worksheet || !cell || cell.type !== 'number') return;

    const trimmedValue = value.trim();
    const numericValue = trimmedValue === '' ? '' : Number(trimmedValue);
    const nextCell: CellDefinition = {
      type: 'number',
      value: numericValue === '' || !Number.isFinite(numericValue) ? '' : numericValue,
    };
    const nextWorksheet: Worksheet = {
      ...worksheet,
      cells: { ...worksheet.cells, [address]: nextCell },
    };

    this.spreadsheetWorksheet.set(nextWorksheet);
    this.spreadsheetCalculation.set(this.spreadsheetService.recalculate(nextWorksheet));
    this.fieldChanged.emit({ fieldKey: address, value: trimmedValue });
  }

  private createRuntimeSpreadsheet(source: Worksheet): Worksheet {
    const cells: Record<string, CellDefinition> = {};
    for (const [address, cell] of Object.entries(source.cells ?? {})) {
      if (cell.type === 'formula') {
        cells[address] = { type: 'formula', value: cell.value };
        continue;
      }

      if (cell.type === 'number') {
        const persistedValue = this.fieldValueByKey(this.testKey, address);
        const numericValue = Number(persistedValue);
        cells[address] = {
          type: 'number',
          value: persistedValue !== '' && Number.isFinite(numericValue) ? numericValue : '',
        };
      } else {
        cells[address] = { ...cell };
      }
    }

    return { rows: source.rows, columns: source.columns, cells };
  }

  private valueAsString(value: unknown): string {
    return value === null || value === undefined ? '' : String(value);
  }

  protected displayLabel(name: string, unit?: string): string {
    return unit ? `${name} (${unit})` : name;
  }

  protected computeOutput(formula: string): string {
    return evaluateCustomFormula(formula, this.inputValues());
  }

  protected hasFormula(formula?: string | null): boolean {
    return (formula ?? '').trim().length > 0;
  }

  protected tableRowInputValues(table: CustomTableDefinition, rowIndex: number): Record<string, string> {
    const values: Record<string, string> = {};
    for (const column of table.columns ?? []) {
      if (column.kind !== 'input') {
        continue;
      }
      const fieldKey = this.tableCellKey(table, rowIndex, column);
      values[this.tableColumnKey(table, column)] = this.inputValues()[fieldKey] ?? '';
    }
    return values;
  }

  private tableCellNumericValue(
    table: CustomTableDefinition,
    rowIndex: number,
    column: CustomTableColumn,
    visiting: Set<string>
  ): number | null {
    const fieldKey = this.tableCellKey(table, rowIndex, column);

    if (column.kind === 'input') {
      const parsed = parseFloat(this.inputValues()[fieldKey] ?? '');
      return Number.isFinite(parsed) ? parsed : null;
    }

    if (!this.hasFormula(column.formula)) {
      const parsed = parseFloat(this.manualTableOutputValue(fieldKey));
      return Number.isFinite(parsed) ? parsed : null;
    }

    const parsed = parseFloat(this.computeTableOutputInternal(table, rowIndex, column, visiting));
    return Number.isFinite(parsed) ? parsed : null;
  }

  protected tableColumnSums(table: CustomTableDefinition, visiting: Set<string>): Record<string, string> {
    const sums: Record<string, string> = {};

    for (const column of table.columns ?? []) {
      const columnKey = this.tableColumnKey(table, column);
      let sum = 0;
      for (const rowIndex of this.tableRowIndices(table.rowCount)) {
        const numericValue = this.tableCellNumericValue(table, rowIndex, column, visiting);
        if (numericValue !== null) {
          sum += numericValue;
        }
      }

      sums[`${columnKey}_sum`] = String(sum);
    }

    return sums;
  }

  private computeTableOutputInternal(
    table: CustomTableDefinition,
    rowIndex: number,
    column: CustomTableColumn,
    visiting: Set<string>
  ): string {
    const formula = (column.formula ?? '').trim();
    if (!formula) {
      const fieldKey = this.tableCellKey(table, rowIndex, column);
      return this.manualTableOutputValue(fieldKey);
    }

    const currentCellKey = this.tableCellKey(table, rowIndex, column);
    if (visiting.has(currentCellKey)) {
      return '';
    }

    const nextVisiting = new Set(visiting);
    nextVisiting.add(currentCellKey);

    const rowValues = this.tableRowInputValues(table, rowIndex);
    const formulaValues = { ...this.tableColumnSums(table, nextVisiting), ...rowValues };
    return evaluateCustomFormula(formula, formulaValues);
  }

  protected computeTableOutput(table: CustomTableDefinition, rowIndex: number, column: CustomTableColumn): string {
    return this.computeTableOutputInternal(table, rowIndex, column, new Set<string>());
  }

  protected manualTableOutputValue(fieldKey: string): string {
    return this.manualOutputValues()[fieldKey] ?? this.fieldValueByKey(this.testKey, fieldKey);
  }

  protected onTableInputChange(fieldKey: string, value: string): void {
    this.onInputChange(fieldKey, value);
  }

  protected onTableManualOutputChange(fieldKey: string, value: string): void {
    this.onManualOutputChange(fieldKey, value);
  }

  protected manualOutputValue(fieldKey: string): string {
    return this.manualOutputValues()[fieldKey] ?? this.fieldValueByKey(this.testKey, fieldKey);
  }

  protected onManualOutputChange(fieldKey: string, value: string): void {
    this.manualOutputValues.update(prev => ({ ...prev, [fieldKey]: value }));
    this.fieldChanged.emit({ fieldKey, value });
  }
}
